/**
 * MessageInput Component — Notion-like design with integrated AI Composer
 */

import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import {
  Send, FileText, ChevronDown,
  Pencil, Check, Sparkles, Loader2,
  FolderKanban, ListChecks, StickyNote, Brain, Copy, RotateCcw, Timer, TimerOff, Smile, Paperclip,
} from 'lucide-react';
import { Tooltip } from 'react-tooltip';
import toast from 'react-hot-toast';
import { supabase } from '@/lib/supabase';

import { ReplyPreview } from './ReplyPreview';
import { AgentSuggestionBar } from './AgentSuggestionBar';
import { MediaButtons } from './MediaButtons';
import { VoiceRecorder } from './VoiceRecorder';
import { ScheduleModal } from './ScheduleModal';
import { ScheduledMessagesPanel } from './ScheduledMessagesPanel';
import { TemplatePicker } from './TemplatePicker';
import { CswBanner } from '@/components/whatsapp2/chat/CswBanner';
import { TemplateSelectorModal, type SelectedTemplatePayload } from '@/components/whatsapp2/chat/TemplateSelectorModal';
import { useWhatsAppSend, useWhatsAppPresence } from '@/lib/hooks/whatsapp';
import { useCloudCsw } from '@/lib/hooks/whatsapp/useCloudCsw';
import { useWhatsAppAgentStore } from '@/lib/stores/whatsappAgentStore';
import { useWhatsAppChatStore } from '@/lib/stores/whatsappChatStore';
import type { Message } from '@/lib/types/whatsappChat';
import type { AgentRequest } from '@/lib/types/whatsappAgent';
import { tip, iconTip } from '@/components/messages/ChatTooltip';
import { EmojiPickerPopover } from '@/components/whatsapp2/shared/EmojiPickerPopover';
import { useWhatsAppChatMetaStore } from '@/lib/stores/whatsappChatMetaStore';
import { useChatCapabilities } from '@/components/whatsapp2/messages/useChatCapabilities';
import { AttachmentPreview } from './AttachmentPreview';
import { useAttachmentStore } from './attachmentStore';
import { filesFromList, renamePastedFile } from './attachments';
import { QuickReplyMenu } from './QuickReplyMenu';
import {
  detectSlashQuery, replaceSlashToken, insertAtCursor, filterTemplates, fillTemplateVariables,
  type TemplateLike,
} from './quickReplies';
import { useLinkedClientVars } from './useLinkedClientVars';
import { useOrgAiEnabled, AI_DISABLED_TOOLTIP } from '@/lib/hooks/useOrgAiEnabled';
import { useUndoSend, UNDO_SECONDS, type PendingSend } from './useUndoSend';

interface MessageInputProps {
  chatId: string;
  chatName?: string;
  replyingTo: Message | null;
  onCancelReply: () => void;
  suggestions?: AgentRequest[];
  isAgentActive?: boolean;
  isAnalyzing?: boolean;
  onCreateTaskFromSuggestion?: (request: AgentRequest) => void;
  onTriggerAnalysis?: () => void;
  onOpenAgentPanel?: () => void;
}

export function MessageInput({
  chatId,
  chatName,
  replyingTo,
  onCancelReply,
  suggestions = [],
  isAgentActive = false,
  isAnalyzing = false,
  onCreateTaskFromSuggestion,
  onTriggerAnalysis,
  onOpenAgentPanel,
}: MessageInputProps) {
  const [text, setText] = useState('');
  const [isMediaOpen, setIsMediaOpen] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  // Cambia a ogni programmazione: il pannello dei programmati si ricarica.
  const [scheduledReloadKey, setScheduledReloadKey] = useState(0);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionedJids, setMentionedJids] = useState<string[]>([]);
  const [mentionIndex, setMentionIndex] = useState(0);

  // Undo send (invio ritardato): logica in useUndoSend
  const [undoSendEnabled, setUndoSendEnabled] = useState(() => {
    try { return localStorage.getItem('whatsapp_undo_send') === 'true'; } catch { return false; }
  });
  // Messaggio ritardato non partito per cambio chat: finisce nella bozza della sua chat.
  const abandonedDraftRef = useRef<{ chatId: string; text: string } | null>(null);

  // Emoji, risposte rapide con "/", cursore salvato per inserire modelli al punto giusto
  const [showEmoji, setShowEmoji] = useState(false);
  const [slash, setSlash] = useState<{ query: string; start: number } | null>(null);
  const [slashIndex, setSlashIndex] = useState(0);
  const selectionRef = useRef<{ start: number; end: number }>({ start: 0, end: 0 });
  const [isDragOver, setIsDragOver] = useState(false);

  // AI Composer state
  const [aiMode, setAiMode] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiGenerated, setAiGenerated] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [aiEditing, setAiEditing] = useState(false);
  const [aiCopied, setAiCopied] = useState(false);
  const [aiIncludeProject, setAiIncludeProject] = useState(true);
  const [aiIncludeTasks, setAiIncludeTasks] = useState(true);
  const [aiIncludeDeal, setAiIncludeDeal] = useState(false);
  const [aiIncludeNotes, setAiIncludeNotes] = useState(false);
  const [aiIncludeSummary, setAiIncludeSummary] = useState(false);

  const { wahaConfig, messages: storeMessages, provider } = useWhatsAppChatStore();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const prevChatIdRef = useRef<string>(chatId);

  // CSW (finestra 24h) — solo per provider Cloud. Per WAHA isCloud=false → no-op.
  const { isCloud, cswState, cswUnknown, refresh: refreshCsw, canSendFreeForm } = useCloudCsw(chatId);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  // Finestra chiusa: si blocca tutto cio' che non e' un modello approvato (testo, allegati, vocale, posizione, contatto).
  // Lettura della finestra non ancora arrivata per questa chat: niente invio libero, ma nessun avviso di "chiusa".
  const cswChecking = isCloud && cswUnknown;
  const cswClosed = !canSendFreeForm && !cswChecking;
  const CSW_CHECKING_MSG = 'Verifico la finestra di 24 ore, un attimo...';
  const CSW_CLOSED_TIP = 'Sono passate piu\' di 24 ore dall\'ultimo messaggio del cliente: allegati, vocali, posizione e contatti non si possono inviare. Scrivigli con un modello approvato.';
  const openTemplatesForClosedWindow = useCallback(() => {
    toast('Finestra di 24 ore chiusa: per ora puoi inviare solo un modello approvato.', { duration: 4000 });
    setShowTemplateSelector(true);
  }, []);

  const handleSendTemplate = useCallback(async (payload: SelectedTemplatePayload) => {
    if (!provider?.sendTemplate) return;
    try {
      await provider.sendTemplate(chatId, {
        templateName: payload.templateName,
        language: payload.language,
        variables: payload.variables,
        displayText: payload.renderedText,
      });
      setShowTemplateSelector(false);
      refreshCsw();
    } catch (err) {
      console.error('[MessageInput] sendTemplate error:', err);
      // Mai alert() nel CRM: blocca la pagina (e i collaudi automatici).
      toast.error(err instanceof Error ? err.message : 'Invio del template non riuscito');
    }
  }, [provider, chatId, refreshCsw]);

  // isSending: invio testo in corso (blocca il doppio invio); isUploading: allegato in caricamento.
  const { sendText, sendMedia, sendVoice, isSending, isUploading } = useWhatsAppSend();
  const capabilities = useChatCapabilities();
  const addAttachments = useAttachmentStore((s) => s.add);
  const clearAttachments = useAttachmentStore((s) => s.clear);
  const templates = useWhatsAppChatMetaStore((s) => s.templates);
  const templatesLoading = useWhatsAppChatMetaStore((s) => s.templatesLoading);
  const fetchTemplates = useWhatsAppChatMetaStore((s) => s.fetchTemplates);
  const { pendingDraftText, setPendingDraftText, assignments, chatSummaries } = useWhatsAppAgentStore();
  const { setDraft, getDraft, clearDraft } = useWhatsAppChatStore();

  // Dati assignment per AI Composer context
  const assignment = assignments.find(a => a.chat_jid === chatId);
  const linkedClientId = assignment?.client?.id || null;
  const projectId = assignment?.project_id || null;
  const dealId = assignment?.deal_id || null;
  const hasSummary = !!chatSummaries.get(chatId);
  // {{nome}} e {{azienda}} dei modelli, dal Lead/Cliente abbinato alla chat
  const clientVars = useLinkedClientVars(linkedClientId);
  // Interruttore IA dell'azienda: spento = composer IA disattivato.
  const aiEnabled = useOrgAiEnabled();

  // Context sources per AI composer
  const aiContextSources = useMemo(() => [
    { key: 'project', label: 'Progetto', icon: FolderKanban, enabled: aiIncludeProject, available: !!projectId, toggle: () => setAiIncludeProject(v => !v) },
    { key: 'tasks', label: 'Task', icon: ListChecks, enabled: aiIncludeTasks, available: !!projectId, toggle: () => setAiIncludeTasks(v => !v) },
    { key: 'deal', label: 'Offerta', icon: FileText, enabled: aiIncludeDeal, available: !!dealId, toggle: () => setAiIncludeDeal(v => !v) },
    { key: 'notes', label: 'Note', icon: StickyNote, enabled: aiIncludeNotes, available: !!linkedClientId, toggle: () => setAiIncludeNotes(v => !v) },
    { key: 'summary', label: 'Riepilogo', icon: Brain, enabled: aiIncludeSummary, available: hasSummary, toggle: () => setAiIncludeSummary(v => !v) },
  ], [aiIncludeProject, aiIncludeTasks, aiIncludeDeal, aiIncludeNotes, aiIncludeSummary, projectId, dealId, linkedClientId, hasSummary]);

  const availableAiSources = aiContextSources.filter(s => s.available);
  const enabledAiCount = availableAiSources.filter(s => s.enabled).length;

  // Partecipanti gruppo per menzioni
  const isGroup = chatId.endsWith('@g.us');
  const groupParticipants = useMemo(() => {
    if (!isGroup) return [];
    const map = new Map<string, string>();
    for (const msg of storeMessages) {
      if (msg.senderJid && msg.senderName && !map.has(msg.senderJid)) {
        map.set(msg.senderJid, msg.senderName);
      }
    }
    return Array.from(map.entries()).map(([jid, name]) => ({ jid, name }));
  }, [isGroup, storeMessages]);

  const mentionResults = useMemo(() => {
    if (mentionQuery === null || !isGroup) return [];
    const q = mentionQuery.toLowerCase();
    return groupParticipants
      .filter(p => p.name.toLowerCase().includes(q))
      .slice(0, 6);
  }, [mentionQuery, groupParticipants, isGroup]);

  // Typing presence
  useWhatsAppPresence({ chatId, text });

  // Salva draft della chat precedente e carica quello della nuova
  useEffect(() => {
    const prevChatId = prevChatIdRef.current;
    if (prevChatId !== chatId) {
      // Se un invio ritardato e' stato fermato dal cambio chat, la bozza e' quella (vedi onAbandon).
      const abandoned = abandonedDraftRef.current;
      abandonedDraftRef.current = null;
      setDraft(prevChatId, abandoned && abandoned.chatId === prevChatId ? abandoned.text : text);
      const savedDraft = getDraft(chatId);
      setText(savedDraft);
      setMentionedJids([]);
      setMentionQuery(null);
      setSlash(null);
      setShowEmoji(false);
      clearAttachments();
      // Reset AI mode on chat change
      setAiMode(false);
      setAiGenerated('');
      setAiPrompt('');
      setAiError(null);
      prevChatIdRef.current = chatId;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatId]);

  // Auto-focus on chat change
  useEffect(() => {
    textareaRef.current?.focus();
  }, [chatId]);

  // Apply pending draft text from agent suggestions / quick actions
  useEffect(() => {
    if (pendingDraftText) {
      setText(pendingDraftText);
      setPendingDraftText(null);
      // Chiudi AI mode se aperto
      setAiMode(false);
      setAiGenerated('');
      textareaRef.current?.focus();
    }
  }, [pendingDraftText, setPendingDraftText]);

  // Auto-resize textarea (min 42px, cresce con il contenuto fino a 120px)
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = '42px';
      const newHeight = Math.max(42, Math.min(textarea.scrollHeight, 120));
      textarea.style.height = `${newHeight}px`;
      textarea.style.overflowY = newHeight >= 120 ? 'auto' : 'hidden';
    }
  }, [text, aiPrompt]);

  const doActualSend = useCallback((msgText: string, mentions: string[]) => {
    clearDraft(chatId);
    sendText(msgText, mentions.length > 0 ? mentions : undefined);
    onCancelReply();
  }, [sendText, onCancelReply, clearDraft, chatId]);

  // Testo corrente letto dal cleanup dell'invio ritardato (gira dopo il cambio chat).
  const textRef = useRef(text);
  textRef.current = text;

  const handleAbandonedSend = useCallback((p: PendingSend) => {
    const merged = [p.text, textRef.current.trim()].filter(Boolean).join('\n');
    abandonedDraftRef.current = { chatId: p.chatId, text: merged };
    setDraft(p.chatId, merged); // copre anche la chiusura del pannello
    toast('Il messaggio in attesa non e\' partito perche\' hai cambiato chat: lo trovi nella bozza della chat di prima.', { duration: 6000 });
  }, [setDraft]);

  const undo = useUndoSend(chatId, doActualSend, handleAbandonedSend);

  const cancelUndoSend = useCallback(() => {
    const p = undo.cancel();
    if (!p) return;
    // Rimette il testo nel campo, davanti a quello eventualmente scritto nel frattempo.
    const restored = [p.text, text.trim()].filter(Boolean).join('\n');
    setText(restored);
    setDraft(chatId, restored);
    setMentionedJids(p.mentions);
    textareaRef.current?.focus();
  }, [undo, text, chatId, setDraft]);

  const handleSend = useCallback(() => {
    // CSW chiusa (Cloud): niente testo libero → forza selezione template.
    if (cswChecking) { toast(CSW_CHECKING_MSG, { duration: 2500 }); return; }
    if (cswClosed) { setShowTemplateSelector(true); return; }
    // Anti doppio invio: testo in partenza o messaggio ritardato gia' in attesa.
    if (!text.trim() || isSending || undo.pending) return;

    const msgText = text.trim();
    const mentions = [...mentionedJids];
    setText('');
    setDraft(chatId, '');
    setMentionedJids([]);
    setMentionQuery(null);
    setSlash(null);
    if (undoSendEnabled) {
      undo.start(msgText, mentions);
    } else {
      doActualSend(msgText, mentions);
    }
  }, [text, isSending, mentionedJids, undoSendEnabled, doActualSend, undo, cswClosed, cswChecking, chatId, setDraft]);

  const toggleUndoSend = useCallback(() => {
    setUndoSendEnabled(prev => {
      const next = !prev;
      try { localStorage.setItem('whatsapp_undo_send', String(next)); } catch { /* storage bloccato */ }
      return next;
    });
  }, []);

  // ====== RISPOSTE RAPIDE ("/"), EMOJI, MODELLI ======

  const slashResults = useMemo(
    () => (slash ? filterTemplates(templates as TemplateLike[], slash.query) : []),
    [slash, templates],
  );

  useEffect(() => {
    if (slash && templates.length === 0 && wahaConfig?.organizationId && !templatesLoading) {
      fetchTemplates(wahaConfig.organizationId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slash !== null, wahaConfig?.organizationId]);

  const rememberSelection = useCallback(() => {
    const ta = textareaRef.current;
    if (ta) selectionRef.current = { start: ta.selectionStart, end: ta.selectionEnd };
  }, []);

  /** Mette il testo nel campo e posiziona il cursore (o seleziona la prima {{variabile}} rimasta). */
  const applyText = useCallback((next: string, cursor: number, insertedFrom?: number) => {
    setText(next);
    setDraft(chatId, next);
    requestAnimationFrame(() => {
      const ta = textareaRef.current;
      if (!ta) return;
      ta.focus();
      const placeholder = insertedFrom !== undefined ? next.slice(insertedFrom, cursor).search(/\{\{\s*\w+\s*\}\}/) : -1;
      if (placeholder >= 0 && insertedFrom !== undefined) {
        const from = insertedFrom + placeholder;
        const to = next.indexOf('}}', from) + 2;
        ta.setSelectionRange(from, to);
        toast('Completa le parti tra parentesi graffe prima di inviare', { duration: 3000 });
      } else {
        ta.setSelectionRange(cursor, cursor);
      }
      selectionRef.current = { start: ta.selectionStart, end: ta.selectionEnd };
    });
  }, [chatId, setDraft]);

  const handleQuickReplyPick = useCallback((t: TemplateLike) => {
    if (!slash) return;
    const filled = fillTemplateVariables(t.content, clientVars as Record<string, string | undefined>);
    const cursor = textareaRef.current?.selectionStart ?? text.length;
    const r = replaceSlashToken(text, slash.start, cursor, filled);
    setSlash(null);
    applyText(r.text, r.cursor, slash.start);
  }, [slash, text, clientVars, applyText]);

  const handleTemplateInsert = useCallback((content: string) => {
    const { start, end } = selectionRef.current;
    const r = insertAtCursor(text, start, end, content);
    setShowTemplatePicker(false);
    applyText(r.text, r.cursor, r.cursor - content.length);
  }, [text, applyText]);

  const handleEmojiSelect = useCallback((emoji: string) => {
    const { start, end } = selectionRef.current;
    const r = insertAtCursor(text, start, end, emoji, false);
    applyText(r.text, r.cursor);
  }, [text, applyText]);

  // ====== ALLEGATI (graffetta, incolla, trascina) ======

  const handleFilesSelected = useCallback((files: File[]) => {
    if (files.length === 0) return;
    if (cswChecking) { toast(CSW_CHECKING_MSG, { duration: 2500 }); return; }
    if (cswClosed) {
      toast.error('La finestra di 24 ore e\' chiusa: per ora puoi inviare solo un modello approvato.');
      return;
    }
    addAttachments(chatId, files);
  }, [addAttachments, chatId, cswClosed, cswChecking]);

  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const files = filesFromList(e.clipboardData?.files);
    if (files.length === 0) return; // testo normale: incolla come sempre
    e.preventDefault();
    handleFilesSelected(files.map((f) => renamePastedFile(f)));
  }, [handleFilesSelected]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    if (!e.dataTransfer?.types?.includes('Files')) return;
    e.preventDefault();
    setIsDragOver(false);
    handleFilesSelected(filesFromList(e.dataTransfer.files));
  }, [handleFilesSelected]);

  // File trascinati sulla lista messaggi con la finestra 24h chiusa (Cloud): non si possono inviare.
  const pendingAttachmentCount = useAttachmentStore((s) => (s.chatId === chatId ? s.items.length : 0));
  useEffect(() => {
    if (cswClosed && pendingAttachmentCount > 0) {
      clearAttachments();
      toast.error('La finestra di 24 ore e\' chiusa: per ora puoi inviare solo un modello approvato.');
    }
  }, [cswClosed, pendingAttachmentCount, clearAttachments]);

  const handleAttachmentSend = useCallback(async (file: File, kind: 'image' | 'video' | 'document', caption: string) => {
    // Finestra chiusa nel frattempo: niente invio (l'anteprima viene svuotata con avviso, vedi sopra).
    if (cswClosed) return;
    if (cswChecking) { toast(CSW_CHECKING_MSG, { duration: 2500 }); return; }
    await sendMedia(file, kind, caption || undefined);
  }, [sendMedia, cswClosed, cswChecking]);

  const handleMentionSelect = useCallback((participant: { jid: string; name: string }) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const cursorPos = textarea.selectionStart;
    const textBeforeCursor = text.slice(0, cursorPos);
    const atIndex = textBeforeCursor.lastIndexOf('@');
    if (atIndex === -1) return;
    const before = text.slice(0, atIndex);
    const after = text.slice(cursorPos);
    const newText = `${before}@${participant.name} ${after}`;
    setText(newText);
    setDraft(chatId, newText);
    setMentionQuery(null);
    setMentionedJids(prev => prev.includes(participant.jid) ? prev : [...prev, participant.jid]);
    const newCursorPos = atIndex + participant.name.length + 2;
    requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    });
  }, [text, chatId, setDraft]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (slash) {
        if (e.key === 'Escape') { e.preventDefault(); setSlash(null); return; }
        if (slashResults.length > 0) {
          if (e.key === 'ArrowDown') { e.preventDefault(); setSlashIndex(i => (i + 1) % slashResults.length); return; }
          if (e.key === 'ArrowUp') { e.preventDefault(); setSlashIndex(i => (i - 1 + slashResults.length) % slashResults.length); return; }
          if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); handleQuickReplyPick(slashResults[Math.min(slashIndex, slashResults.length - 1)]); return; }
        }
      }
      if (mentionQuery !== null && mentionResults.length > 0) {
        if (e.key === 'ArrowDown') { e.preventDefault(); setMentionIndex(i => (i + 1) % mentionResults.length); return; }
        if (e.key === 'ArrowUp') { e.preventDefault(); setMentionIndex(i => (i - 1 + mentionResults.length) % mentionResults.length); return; }
        if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); handleMentionSelect(mentionResults[mentionIndex]); return; }
        if (e.key === 'Escape') { e.preventDefault(); setMentionQuery(null); return; }
      }
      // Esc con una risposta in corso annulla solo la risposta, non chiude il pannello.
      if (e.key === 'Escape' && replyingTo) { e.preventDefault(); onCancelReply(); return; }
      // Esc con testo scritto: non chiude il pannello (si perderebbe di vista la bozza).
      if (e.key === 'Escape' && text.trim()) { e.preventDefault(); return; }
      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
    },
    [handleSend, mentionQuery, mentionResults, mentionIndex, handleMentionSelect, replyingTo, onCancelReply, slash, slashResults, slashIndex, handleQuickReplyPick, text]
  );

  const handleTextChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setText(val);
    setDraft(chatId, val);
    selectionRef.current = { start: e.target.selectionStart, end: e.target.selectionEnd };
    const nextSlash = detectSlashQuery(val, e.target.selectionStart);
    setSlash(nextSlash);
    if (nextSlash) setSlashIndex(0);
    if (isGroup) {
      const cursorPos = e.target.selectionStart;
      const textBeforeCursor = val.slice(0, cursorPos);
      const atMatch = textBeforeCursor.match(/@(\S*)$/);
      if (atMatch) { setMentionQuery(atMatch[1]); setMentionIndex(0); }
      else { setMentionQuery(null); }
    }
  }, [chatId, setDraft, isGroup]);

  const handleVoiceSend = useCallback((audio: Blob) => {
    setIsRecording(false);
    // La finestra puo' essersi chiusa durante la registrazione: Meta rifiuterebbe il vocale.
    if (cswClosed) { openTemplatesForClosedWindow(); return; }
    if (cswChecking) { toast(CSW_CHECKING_MSG, { duration: 2500 }); return; }
    sendVoice(audio);
  }, [sendVoice, cswClosed, cswChecking, openTemplatesForClosedWindow]);

  // ====== AI COMPOSER HANDLERS ======

  const handleAiGenerate = useCallback(async () => {
    if (!aiPrompt.trim() || aiGenerating || !wahaConfig?.organizationId) return;
    setAiGenerating(true);
    setAiError(null);
    setAiGenerated('');
    setAiEditing(false);

    try {
      const { data, error: fnError } = await supabase.functions.invoke('whatsapp-compose-message', {
        body: {
          organization_id: wahaConfig.organizationId,
          prompt: aiPrompt.trim(),
          contact_name: chatName || '',
          context: {
            include_project_status: aiIncludeProject && !!projectId,
            include_tasks: aiIncludeTasks && !!projectId,
            include_deal: aiIncludeDeal && !!dealId,
            include_notes: aiIncludeNotes && !!linkedClientId,
            include_summary: aiIncludeSummary && hasSummary,
            project_id: projectId || undefined,
            deal_id: dealId || undefined,
            client_id: linkedClientId || undefined,
            chat_jid: chatId,
          },
        },
      });
      if (fnError) throw new Error(fnError.message);
      if (data?.error) throw new Error(data.error);
      setAiGenerated(data.message || '');
    } catch (err: any) {
      setAiError(err.message || 'Errore nella generazione');
    } finally {
      setAiGenerating(false);
    }
  }, [aiPrompt, aiGenerating, wahaConfig?.organizationId, chatName, chatId, projectId, dealId, linkedClientId, hasSummary, aiIncludeProject, aiIncludeTasks, aiIncludeDeal, aiIncludeNotes, aiIncludeSummary]);

  const handleAiInsert = useCallback(() => {
    if (!aiGenerated.trim()) return;
    setText(aiGenerated.trim());
    setAiGenerated('');
    setAiPrompt('');
    setAiMode(false);
    textareaRef.current?.focus();
  }, [aiGenerated]);

  const handleAiCopy = useCallback(() => {
    navigator.clipboard.writeText(aiGenerated);
    setAiCopied(true);
    setTimeout(() => setAiCopied(false), 1500);
  }, [aiGenerated]);

  const hasText = text.trim().length > 0;

  // Esc esce dalla scrittura con l'AI ovunque sia il cursore: dopo "genera" il pulsante si
  // disattiva e il fuoco finisce sulla pagina, dove l'Esc della barra non arrivava (27/09/2026).
  // In fase di cattura e con preventDefault, cosi' il pannello Messaggi non si chiude.
  useEffect(() => {
    if (!aiMode) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      e.preventDefault();
      setAiMode(false);
      setAiGenerated('');
      setAiError(null);
      setAiPrompt('');
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [aiMode]);

  return (
    <div
      className={`relative flex-shrink-0 bg-[#ECE5DD] dark:bg-[#1B2B34] border-t border-[rgba(55,53,47,0.09)] dark:border-white/[0.06] ${isDragOver ? 'ring-2 ring-inset ring-[#25D366]' : ''}`}
      onDragOver={(e) => { if (e.dataTransfer?.types?.includes('Files') && !aiMode) { e.preventDefault(); setIsDragOver(true); } }}
      onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsDragOver(false); }}
      onDrop={handleDrop}
    >
      {isDragOver && (
        <div className="absolute inset-0 z-40 flex items-center justify-center gap-2 bg-[#25D366]/10 text-sm font-medium text-[#128C7E] pointer-events-none">
          <Paperclip className="w-4 h-4" /> Rilascia per allegare
        </div>
      )}
      {/* Agent compact indicator */}
      <AgentSuggestionBar
        suggestions={suggestions}
        isAgentActive={isAgentActive}
        isAnalyzing={isAnalyzing}
        onTriggerAnalysis={onTriggerAnalysis}
        onOpenAgentPanel={onOpenAgentPanel}
      />

      {/* Banner finestra 24h (CSW) — solo provider Cloud. Chiusa → CTA "Invia template". */}
      {isCloud && (
        <CswBanner state={cswState} onOpenTemplates={() => setShowTemplateSelector(true)} onExpired={refreshCsw} />
      )}

      {/* Messaggi programmati di questa chat */}
      <ScheduledMessagesPanel chatId={chatId} organizationId={wahaConfig?.organizationId} reloadKey={scheduledReloadKey} />

      {/* Reply preview */}
      {replyingTo && (
        <ReplyPreview message={replyingTo} onCancel={onCancelReply} />
      )}

      {/* === AI Generated preview (above input when result ready) === */}
      {aiMode && aiGenerated && (
        <div className="px-3 pt-2.5 pb-1">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[9px] font-semibold text-[#A4A4A0] uppercase tracking-wider">Anteprima AI</span>
            <div className="flex items-center gap-0.5">
              <button onClick={handleAiGenerate} disabled={aiGenerating}
                className="p-1 rounded-md hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/[0.06] text-[#A4A4A0] hover:text-[#37352F] dark:hover:text-white transition-colors disabled:opacity-40"
                {...tip('Rigenera')}>
                <RotateCcw className="w-3 h-3" />
              </button>
              <button onClick={() => setAiEditing(!aiEditing)}
                className={`p-1 rounded-md transition-colors ${aiEditing ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-600' : 'hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/[0.06] text-[#A4A4A0] hover:text-[#37352F] dark:hover:text-white'}`}
                {...tip('Modifica')}>
                <Pencil className="w-3 h-3" />
              </button>
              <button onClick={handleAiCopy}
                className="p-1 rounded-md hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/[0.06] text-[#A4A4A0] hover:text-[#37352F] dark:hover:text-white transition-colors"
                {...tip('Copia')}>
                {aiCopied ? <Check className="w-3 h-3 text-emerald-500" /> : <Copy className="w-3 h-3" />}
              </button>
            </div>
          </div>

          {aiEditing ? (
            <textarea
              value={aiGenerated}
              onChange={e => setAiGenerated(e.target.value)}
              rows={4}
              className="w-full text-[11px] resize-none bg-white dark:bg-[#191919] border border-purple-300 dark:border-purple-600 rounded-lg px-2.5 py-2 outline-none text-[#37352F] dark:text-white/90 leading-relaxed"
            />
          ) : (
            <div className="rounded-lg bg-purple-50/60 dark:bg-purple-900/10 border border-purple-200/60 dark:border-purple-800/30 px-3 py-2 max-h-[140px] overflow-y-auto">
              <p className="text-[11px] text-[#37352F] dark:text-white/90 leading-[1.7] whitespace-pre-wrap">{aiGenerated}</p>
            </div>
          )}

          {/* Insert into input */}
          <button
            onClick={handleAiInsert}
            className="w-full flex items-center justify-center gap-1.5 mt-2 mb-1 px-3 py-1.5 text-[11px] font-medium rounded-lg transition-all
              bg-[#37352F] text-white hover:bg-[#2f2d28] dark:bg-white dark:text-[#37352F] dark:hover:bg-white/90"
          >
            <Send className="w-3 h-3" />
            Usa questo testo
          </button>
        </div>
      )}

      {/* === MAIN INPUT AREA === */}
      <div className="px-3 py-2.5">
        {/* AI mode: context source pills */}
        {aiMode && !aiGenerated && availableAiSources.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 mb-2">
            <span className="text-[9px] font-semibold text-purple-500 uppercase tracking-wider mr-0.5">Contesto</span>
            {availableAiSources.map(source => {
              const Icon = source.icon;
              return (
                <button
                  key={source.key}
                  onClick={source.toggle}
                  className={`flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium transition-all
                    ${source.enabled
                      ? 'bg-purple-600 text-white dark:bg-purple-500'
                      : 'text-[#A4A4A0] bg-[rgba(55,53,47,0.04)] dark:bg-white/[0.04] hover:bg-purple-50 dark:hover:bg-purple-900/20 hover:text-purple-500'
                    }`}
                >
                  <Icon className="w-3 h-3" />
                  {source.label}
                </button>
              );
            })}
          </div>
        )}

        {/* AI error */}
        {aiMode && aiError && (
          <p className="text-[10px] text-red-500 mb-1.5">{aiError}</p>
        )}

        {/* Toolbar row */}
        <div className="flex items-center gap-0.5 mb-1.5">
          {/* Media — hidden in AI mode */}
          {!aiMode && (
            <MediaButtons
              isOpen={isMediaOpen}
              onToggle={() => setIsMediaOpen(!isMediaOpen)}
              onFilesSelected={handleFilesSelected}
              capabilities={capabilities}
              disabledReason={cswClosed ? CSW_CLOSED_TIP : undefined}
              onBlockedClick={openTemplatesForClosedWindow}
            />
          )}

          {/* Emoji — nascosto in AI mode */}
          {!aiMode && (
            <div className="relative">
              <button
                type="button"
                onClick={() => { rememberSelection(); setShowEmoji((v) => !v); }}
                className={`p-1.5 rounded-md transition-colors ${showEmoji ? 'bg-[#2383E2]/10 text-[#2383E2]' : 'hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/[0.06] text-[#A4A4A0] hover:text-[#787774] dark:hover:text-white/70'}`}
                aria-expanded={showEmoji}
                {...iconTip('Inserisci un\'emoji')}
              >
                <Smile className="w-4 h-4" />
              </button>
              {showEmoji && (
                <EmojiPickerPopover align="left" keepOpen onEmojiSelect={handleEmojiSelect} onClose={() => setShowEmoji(false)} />
              )}
            </div>
          )}

          {/* Template — hidden in AI mode */}
          {!aiMode && (
            <div className="relative">
              <button
                onClick={() => { rememberSelection(); setShowTemplatePicker((v) => !v); }}
                className={`p-1.5 rounded-md transition-colors ${showTemplatePicker ? 'bg-[#2383E2]/10 text-[#2383E2]' : 'hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/[0.06] text-[#A4A4A0] hover:text-[#787774] dark:hover:text-white/70'}`}
                {...iconTip('Inserisci un modello di messaggio salvato (oppure digita / nel campo)')}
              >
                <FileText className="w-4 h-4" />
              </button>
              {showTemplatePicker && (
                <div className="absolute bottom-full left-0 mb-2 z-50">
                  <TemplatePicker
                    onInsert={handleTemplateInsert}
                    onClose={() => setShowTemplatePicker(false)}
                    initialValues={clientVars as Record<string, string | undefined>}
                  />
                </div>
              )}
            </div>
          )}

          {/* AI Compose toggle */}
          <button
            onClick={() => {
              const next = !aiMode;
              setAiMode(next);
              if (!next) { setAiGenerated(''); setAiError(''); setAiPrompt(''); }
              setTimeout(() => textareaRef.current?.focus(), 50);
            }}
            disabled={!aiEnabled && !aiMode}
            className={`p-1.5 rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${aiMode ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400' : 'hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/[0.06] text-[#A4A4A0] hover:text-purple-500'}`}
            data-tooltip-id="input-toolbar-tip"
            data-tooltip-content={aiMode ? 'Torna alla scrittura normale (Esc)' : !aiEnabled ? AI_DISABLED_TOOLTIP : 'Scrivi il messaggio con l\'AI: descrivi cosa dire, poi controlli la bozza'}
          >
            <Sparkles className="w-4 h-4" />
          </button>

          {/* Undo send toggle */}
          {!aiMode && (
            <button
              onClick={toggleUndoSend}
              className={`p-1.5 rounded-md transition-colors ${undoSendEnabled ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400' : 'hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/[0.06] text-[#A4A4A0] hover:text-[#787774] dark:hover:text-white/70'}`}
              data-tooltip-id="input-toolbar-tip"
              data-tooltip-content={undoSendEnabled ? 'Invio ritardato di 5 secondi per poterlo annullare: attivo. Clicca per spegnerlo' : 'Invio immediato. Clicca per ritardarlo di 5 secondi e poterlo annullare'}
            >
              {undoSendEnabled ? <Timer className="w-4 h-4" /> : <TimerOff className="w-4 h-4" />}
            </button>
          )}

          {aiMode && (
            <span className="text-[10px] text-purple-500 font-medium ml-0.5">Scrivi con l'AI</span>
          )}

          <div className="flex-1" />
        </div>

        {/* Allegato in caricamento */}
        {isUploading && (
          <div className="flex items-center gap-2 mb-1.5 px-3 py-1.5 text-xs text-[#787774]" role="status">
            <Loader2 className="w-3.5 h-3.5 animate-spin text-[#2383E2]" /> Caricamento dell'allegato in corso...
          </div>
        )}

        {/* Undo send countdown bar */}
        {undo.pending && undo.countdown > 0 && (
          <div className="flex items-center gap-2 mb-1.5 px-3 py-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/40 rounded-lg">
            <div className="flex-1 flex items-center gap-2 min-w-0">
              <div className="relative w-6 h-6 flex-shrink-0">
                <svg className="w-6 h-6 -rotate-90" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" className="text-amber-200 dark:text-amber-800" strokeWidth="2" />
                  <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" className="text-amber-500" strokeWidth="2"
                    strokeDasharray={`${(undo.countdown / UNDO_SECONDS) * 62.83} 62.83`}
                    strokeLinecap="round" />
                </svg>
                <span className="absolute inset-0 flex items-center justify-center text-[9px] font-bold text-amber-600 dark:text-amber-400">{undo.countdown}</span>
              </div>
              <span className="text-xs text-amber-700 dark:text-amber-300 truncate">
                Invio tra {undo.countdown}s...
              </span>
            </div>
            <button
              onClick={cancelUndoSend}
              className="flex-shrink-0 px-3 py-1 text-xs font-medium rounded-md bg-amber-500 hover:bg-amber-600 text-white transition-colors"
            >
              Annulla
            </button>
          </div>
        )}

        {/* Input row — grid, una riga, altezza 40px */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '8px', minHeight: '42px', alignItems: 'end' }}>
          <div style={{ position: 'relative' }}>
            {/* Risposte rapide con "/" */}
            {!aiMode && slash && (
              <QuickReplyMenu
                items={slashResults}
                activeIndex={Math.min(slashIndex, Math.max(0, slashResults.length - 1))}
                loading={templatesLoading}
                query={slash.query}
                onPick={handleQuickReplyPick}
                onHover={setSlashIndex}
              />
            )}
            {/* Mention autocomplete dropdown */}
            {!aiMode && mentionQuery !== null && mentionResults.length > 0 && (
              <div className="absolute bottom-full left-0 right-0 mb-1 bg-white dark:bg-[#252525] border border-[rgba(55,53,47,0.09)] dark:border-white/[0.06] rounded-lg shadow-lg overflow-hidden z-50">
                {mentionResults.map((p, i) => (
                  <button
                    key={p.jid}
                    onClick={() => handleMentionSelect(p)}
                    className={`w-full px-3 py-2 text-left text-[12px] flex items-center gap-2 transition-colors ${
                      i === mentionIndex
                        ? 'bg-[rgba(55,53,47,0.06)] dark:bg-white/[0.06] text-[#37352F] dark:text-white'
                        : 'text-[#37352F] dark:text-white/90 hover:bg-[rgba(55,53,47,0.03)] dark:hover:bg-white/[0.03]'
                    }`}
                  >
                    <span className="w-5 h-5 rounded-full bg-[rgba(55,53,47,0.06)] dark:bg-white/[0.06] flex items-center justify-center text-[10px] font-medium text-[#787774] dark:text-white/70">
                      {p.name.charAt(0).toUpperCase()}
                    </span>
                    <span className="truncate">{p.name}</span>
                  </button>
                ))}
              </div>
            )}
            <textarea
              ref={textareaRef}
              value={aiMode ? aiPrompt : text}
              onChange={aiMode
                ? (e) => setAiPrompt(e.target.value)
                : handleTextChange
              }
              onKeyDown={aiMode
                ? (e) => {
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); handleAiGenerate(); }
                    // preventDefault: senza, lo stesso Esc chiudeva anche tutto il pannello Messaggi.
                    if (e.key === 'Escape') { e.preventDefault(); setAiMode(false); setAiGenerated(''); setAiError(null); setAiPrompt(''); }
                  }
                : handleKeyDown
              }
              onPaste={aiMode ? undefined : handlePaste}
              onSelect={rememberSelection}
              onBlur={rememberSelection}
              placeholder={aiMode
                ? 'Descrivi il messaggio da generare... (⌘+Enter)'
                : cswChecking
                  ? 'Verifico la finestra di 24 ore...'
                : cswClosed
                  ? 'Finestra di 24 ore chiusa: invia un modello approvato'
                  : 'Scrivi un messaggio... (digita / per le risposte rapide)'}
              rows={1}
              style={{ minHeight: '42px', maxHeight: '120px', display: 'block', boxSizing: 'border-box' }}
              className={`w-full px-3 py-[9px] rounded-lg text-[15px] leading-[22px] text-[#37352F] dark:text-white/90 placeholder:text-[#A4A4A0] resize-none focus:outline-none ${
                aiMode
                  ? 'bg-purple-50/40 dark:bg-purple-900/10 border-2 border-purple-400 dark:border-purple-500 focus:border-purple-500 dark:focus:border-purple-400'
                  : 'bg-white dark:bg-[#2A3942] border border-[rgba(55,53,47,0.09)] dark:border-white/[0.06] focus:border-[rgba(55,53,47,0.2)] dark:focus:border-white/15'
              }`}
              // Non si disabilita durante l'invio: il campo perderebbe il focus a ogni messaggio.
              disabled={isRecording || aiGenerating || cswClosed}
            />
          </div>

          {/* Action button */}
          {aiMode ? (
            <button
              onClick={handleAiGenerate}
              disabled={!aiPrompt.trim() || aiGenerating || !aiEnabled}
              title={!aiEnabled ? AI_DISABLED_TOOLTIP : undefined}
              style={{ height: '42px', width: '40px', display: 'grid', placeItems: 'center' }}
              className="rounded-lg bg-purple-600 hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600 disabled:opacity-40 transition-colors"
            >
              {aiGenerating
                ? <Loader2 className="w-4 h-4 text-white animate-spin" />
                : <Sparkles className="w-4 h-4 text-white" />
              }
            </button>
          ) : hasText ? (
            <div style={{ height: '42px', display: 'inline-flex' }}>
              <button
                onClick={handleSend}
                disabled={isSending || !!undo.pending}
                style={{ height: '42px', display: 'grid', placeItems: 'center', padding: '0 10px' }}
                className="rounded-l-lg bg-[#37352F] hover:bg-[#2f2d28] dark:bg-white dark:hover:bg-white/90 disabled:opacity-50 transition-colors"
                data-tooltip-id="input-toolbar-tip"
                data-tooltip-content="Invia"
              >
                <Send className="w-4 h-4 text-white dark:text-[#37352F]" />
              </button>
              <button
                onClick={() => setShowSchedule(true)}
                style={{ height: '42px', display: 'grid', placeItems: 'center', padding: '0 4px' }}
                className="border-l border-white/20 dark:border-[#37352F]/20 rounded-r-lg bg-[#37352F] hover:bg-[#2f2d28] dark:bg-white dark:hover:bg-white/90 transition-colors"
                data-tooltip-id="input-toolbar-tip"
                data-tooltip-content="Programma invio"
              >
                <ChevronDown className="w-3 h-3 text-white dark:text-[#37352F]" />
              </button>
            </div>
          ) : (
            <div style={{ height: '42px', width: '40px', display: 'grid', placeItems: 'center' }}>
              <VoiceRecorder
                isRecording={isRecording}
                onStartRecording={() => setIsRecording(true)}
                onSend={handleVoiceSend}
                onCancel={() => setIsRecording(false)}
                disabledReason={cswClosed ? CSW_CLOSED_TIP : undefined}
                onBlockedClick={openTemplatesForClosedWindow}
              />
            </div>
          )}
        </div>
      </div>

      {/* Shared tooltip for toolbar buttons */}
      <Tooltip id="input-toolbar-tip" place="top" delayShow={300} className="!text-xs !py-1 !px-2 !rounded-md" />

      {/* Schedule Modal */}
      {showSchedule && (
        <ScheduleModal
          chatId={chatId}
          messageText={text}
          onClose={() => setShowSchedule(false)}
          onScheduled={() => {
            clearDraft(chatId);
            setText('');
            setShowSchedule(false);
            onCancelReply();
            setScheduledReloadKey((k) => k + 1);
          }}
        />
      )}

      {/* Selettore template HSM (Cloud) — aperto da CSW chiusa o dal banner */}
      {showTemplateSelector && wahaConfig?.configId && (
        <TemplateSelectorModal
          isOpen={showTemplateSelector}
          configId={wahaConfig.configId}
          prefillVariables={chatName ? { '1': chatName } : undefined}
          onClose={() => setShowTemplateSelector(false)}
          onSend={handleSendTemplate}
        />
      )}

      {/* Anteprima allegati (graffetta, incolla, trascina) */}
      <AttachmentPreview chatId={chatId} chatName={chatName} onSend={handleAttachmentSend} />
    </div>
  );
}
